import React, { useCallback, useEffect, useRef, useState } from "react";
import Popup from "devextreme-react/popup";
import Form, { Item, Label, RequiredRule } from "devextreme-react/form";
import type { default as FormInstance } from "devextreme/ui/form";
import notify from "devextreme/ui/notify";
import axios, { AxiosError } from "axios";
import * as S from "../ComponentesGarantia/ComponentesGarantia.styles";
import * as GridS from "./Grid.styles";
import { ApiError } from "../../../services/apiError";
import { Productos, ProductoCatalogosDto } from "../../../dtos/Productos";
import { productosService } from "../../../services/productosService";
import { envasesService, CatalogosEnvase } from "../../../services/envasesService";

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    return {
      message:
        error.response?.data?.message || "Ocurrió un error en el servidor.",
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return { message: "Ocurrió un error inesperado. Revisa tu conexión de red." };
};

type ProductoCatalogosSectionProps = {
  product: Productos;
};

const ProductoCatalogosSection: React.FC<ProductoCatalogosSectionProps> = ({
  product,
}) => {
  const [catalogos, setCatalogos] = useState<CatalogosEnvase | null>(null);
  const [editVisible, setEditVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<ProductoCatalogosDto>({
    codigoCuerpo: product.codigoCuerpo ?? null,
    codigoCapuchon: product.codigoCapuchon ?? null,
    codigoCuello: product.codigoCuello ?? null,
    codigoEstarcido: product.codigoEstarcido ?? null,
    codigoOjiva: product.codigoOjiva ?? null,
    idValvula: product.idValvula ?? null,
  });
  const [view, setView] = useState<Productos>(product);
  const formRef = useRef<FormInstance | null>(null);

  useEffect(() => {
    envasesService
      .loadCatalogosEnvase()
      .then(setCatalogos)
      .catch((err) => notify(handleApiError(err).message, "error", 3000));
  }, []);

  const openEdit = useCallback(() => {
    setConfig({
      codigoCuerpo: view.codigoCuerpo ?? null,
      codigoCapuchon: view.codigoCapuchon ?? null,
      codigoCuello: view.codigoCuello ?? null,
      codigoEstarcido: view.codigoEstarcido ?? null,
      codigoOjiva: view.codigoOjiva ?? null,
      idValvula: view.idValvula ?? null,
    });
    setEditVisible(true);
  }, [view]);

  const handleSave = async () => {
    const form = formRef.current;
    if (!form) return;
    const validation = await form.validate();
    if (!validation.isValid) {
      notify("Configure todos los catálogos.", "warning", 2500);
      return;
    }
    const data = form.option("formData") as ProductoCatalogosDto;
    if (product.codigo == null) return;
    setSaving(true);
    try {
      const actualizado = await productosService.configurarCatalogos(
        product.codigo,
        data,
      );
      setView(actualizado);
      notify("Configuración de catálogos guardada.", "success", 2500);
      setEditVisible(false);
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setSaving(false);
    }
  };

  const renderValor = (codigo?: string | null, extra?: string | null) => {
    if (!codigo) return "Sin configurar";
    return extra ? `${codigo} — ${extra}` : codigo;
  };

  const completo =
    !!view.codigoCuerpo &&
    !!view.codigoCapuchon &&
    !!view.codigoCuello &&
    !!view.codigoEstarcido &&
    !!view.codigoOjiva &&
    view.idValvula != null;

  return (
    <>
      <GridS.DevExtremeModalGlobalStyles />
      <S.SectionToolbar>
        <span
          style={{
            alignSelf: "center",
            fontSize: 13,
            fontWeight: 600,
            color: completo ? "#16a34a" : "#b45309",
          }}
        >
          {completo
            ? "Producto completo (catálogos configurados)"
            : "Catálogos incompletos"}
        </span>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={openEdit}
          style={{
            background: "#1d4ed8",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "8px 16px",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          Configurar catálogos
        </button>
      </S.SectionToolbar>

      <GridS.DetailGrid>
        <GridS.DetailItem>
          <GridS.DetailLabel>Cuerpo</GridS.DetailLabel>
          <GridS.DetailValue>
            {renderValor(view.codigoCuerpo, view.colorCuerpo)}
          </GridS.DetailValue>
        </GridS.DetailItem>
        <GridS.DetailItem>
          <GridS.DetailLabel>Capuchón</GridS.DetailLabel>
          <GridS.DetailValue>
            {renderValor(view.codigoCapuchon, view.colorCapuchon)}
          </GridS.DetailValue>
        </GridS.DetailItem>
        <GridS.DetailItem>
          <GridS.DetailLabel>Cuello</GridS.DetailLabel>
          <GridS.DetailValue>
            {renderValor(view.codigoCuello, view.colorCuello)}
          </GridS.DetailValue>
        </GridS.DetailItem>
        <GridS.DetailItem>
          <GridS.DetailLabel>Estarcido</GridS.DetailLabel>
          <GridS.DetailValue>
            {renderValor(view.codigoEstarcido, view.colorEstarcido)}
          </GridS.DetailValue>
        </GridS.DetailItem>
        <GridS.DetailItem>
          <GridS.DetailLabel>Ojiva</GridS.DetailLabel>
          <GridS.DetailValue>
            {renderValor(view.codigoOjiva, view.colorOjiva)}
          </GridS.DetailValue>
        </GridS.DetailItem>
        <GridS.DetailItem>
          <GridS.DetailLabel>Válvula</GridS.DetailLabel>
          <GridS.DetailValue>
            {view.idValvula != null ? view.valvula ?? view.idValvula : "Sin configurar"}
          </GridS.DetailValue>
        </GridS.DetailItem>
      </GridS.DetailGrid>

      <Popup
        visible={editVisible}
        onHiding={() => setEditVisible(false)}
        showTitle
        title="Configurar catálogos del producto"
        width={520}
        height="auto"
        className="custom-form-popup"
      >
        <S.FormContainer>
          <Form
            formData={config}
            onInitialized={(e) => {
              formRef.current = e.component ?? null;
            }}
          >
            <Item
              dataField="codigoCuerpo"
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: catalogos?.cuerpos ?? [],
                valueExpr: "codigo",
                displayExpr: (i: { codigo: string; color?: string }) =>
                  i ? (i.color ? `${i.codigo} — ${i.color}` : i.codigo) : "",
                searchEnabled: true,
                placeholder: "Seleccione cuerpo",
              }}
            >
              <Label text="Cuerpo" />
              <RequiredRule message="El cuerpo es obligatorio." />
            </Item>
            <Item
              dataField="codigoCapuchon"
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: catalogos?.capuchones ?? [],
                valueExpr: "codigo",
                displayExpr: (i: { codigo: string; color?: string }) =>
                  i ? (i.color ? `${i.codigo} — ${i.color}` : i.codigo) : "",
                searchEnabled: true,
                placeholder: "Seleccione capuchón",
              }}
            >
              <Label text="Capuchón" />
              <RequiredRule message="El capuchón es obligatorio." />
            </Item>
            <Item
              dataField="codigoCuello"
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: catalogos?.cuellos ?? [],
                valueExpr: "codigo",
                displayExpr: (i: { codigo: string; color?: string }) =>
                  i ? (i.color ? `${i.codigo} — ${i.color}` : i.codigo) : "",
                searchEnabled: true,
                placeholder: "Seleccione cuello",
              }}
            >
              <Label text="Cuello" />
              <RequiredRule message="El cuello es obligatorio." />
            </Item>
            <Item
              dataField="codigoEstarcido"
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: catalogos?.estarcidos ?? [],
                valueExpr: "codigo",
                displayExpr: (i: { codigo: string; color?: string }) =>
                  i ? (i.color ? `${i.codigo} — ${i.color}` : i.codigo) : "",
                searchEnabled: true,
                placeholder: "Seleccione estarcido",
              }}
            >
              <Label text="Estarcido" />
              <RequiredRule message="El estarcido es obligatorio." />
            </Item>
            <Item
              dataField="codigoOjiva"
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: catalogos?.ojivas ?? [],
                valueExpr: "codigo",
                displayExpr: (i: { codigo: string; color?: string }) =>
                  i ? (i.color ? `${i.codigo} — ${i.color}` : i.codigo) : "",
                searchEnabled: true,
                placeholder: "Seleccione ojiva",
              }}
            >
              <Label text="Ojiva" />
              <RequiredRule message="La ojiva es obligatoria." />
            </Item>
            <Item
              dataField="idValvula"
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: catalogos?.valvulas ?? [],
                valueExpr: "idValvula",
                displayExpr: "descripcion",
                searchEnabled: true,
                placeholder: "Seleccione válvula",
              }}
            >
              <Label text="Válvula" />
              <RequiredRule message="La válvula es obligatoria." />
            </Item>
          </Form>
        </S.FormContainer>
        <S.PopupFooter>
          <S.CancelButton onClick={() => setEditVisible(false)}>
            Cancelar
          </S.CancelButton>
          <S.SaveButton onClick={handleSave} disabled={saving}>
            {saving ? "Guardando..." : "Guardar"}
          </S.SaveButton>
        </S.PopupFooter>
      </Popup>
    </>
  );
};

export default ProductoCatalogosSection;
